/**
 * La prueba del asistente, en «Revisar y mandar»: el número (se recuerda en este navegador), el envío, el estado que se
 * consulta cada 3 s durante un minuto y «Seguir sin probar». Sin una que llegó (o sin «Seguir sin probar»), no se manda
 * (`useConfirmarCampana`).
 *
 * - Un 200 de la prueba no es un éxito por sí solo: Twilio rechaza con 200 y `estado: "fallido"`. El título sale de
 *   `tituloPrueba` (logic/chequeo.ts) y lo que deja seguir es `pruebaDejaSeguir`: un recibo de que llegó (o simulado).
 * - La prueba no es parte de la campaña: quien carga el borrador la vuelve a poner (`restaurar`, la del viaje al
 *   editor; `retomar`, la de la auditoría) y quien cambia la plantilla la borra (`reiniciar`).
 * - Lo que se muestra combina la respuesta del POST con la última consulta (`vista`). Una consulta que vuelve tarde, de
 *   una prueba anterior, no pisa la de ahora (`pruebaIdRef`, en la consulta de cada 3 s y en la de una vez).
 * - Mandar una prueba desmarca «Seguir sin probar» (29/10/2026): se marcó para no probar, y ahora se probó. Si esa prueba
 *   falla, para mandar sin probar hay que volver a marcarlo a propósito (y al lado dice que la prueba falló). Antes
 *   seguía marcado y el botón quedaba prendido con una prueba fallida.
 */

import { useCallback, useRef, useState } from "react";
import type { EstadoPrueba, ResultadoPrueba } from "../../types";
import { oficialApi } from "../../api";
import { usePolling } from "../usePolling";
import { pruebaDejaSeguir, pruebaTerminada, tituloPrueba } from "../../logic/chequeo";
import { TEXTO_ERROR_CLAVE, esErrorDeClave, leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import type { Lectura } from "../../logic/respuesta";
import { textoRechazoPlantilla, textoSaldoInsuficientePrueba } from "../../logic/textos";
import type { Aviso } from "../../logic/asistente";

const RUTA_CONFIG = "/activa/oficial/configuracion";
const NUMERO_PRUEBA_KEY = "deenex-activa:oficial:numero-prueba";

function leerStorage(k: string): string {
  try { return localStorage.getItem(k) || ""; } catch { return ""; }
}
function guardarStorage(k: string, v: string): void {
  try { localStorage.setItem(k, v); } catch { /* sin storage: vale para esta pantalla */ }
}

/** Texto de un rechazo 422 de la prueba (SPEC-frontend §10.2, paso 4). */
function avisoRechazoPrueba(l: Extract<Lectura<unknown>, { ok: false }>): Aviso {
  switch (l.code) {
    case "DNC": return { texto: "Ese número está en la lista de no contactar." };
    // Cuánto falta (el backend lo manda desde que el corte lo calcula) y dónde se carga: «No hay saldo para la
    // prueba» a secas dejaba adivinando el monto.
    case "SALDO_INSUFICIENTE": return { texto: textoSaldoInsuficientePrueba(l.extra.faltanteUSD), link: { label: "Cargar saldo", ruta: `${RUTA_CONFIG}?seccion=saldo` } };
    case "CUPO_AGOTADO": return { texto: "Ya se usó el cupo de Meta de hoy." };
    case "PLANTILLA_NO_APROBADA": return { texto: "La plantilla dejó de estar aprobada." };
    case "PLANTILLA_SIMULADA":
    case "PLANTILLA_DE_OTRA_CUENTA":
      return { texto: textoRechazoPlantilla(l.code)! };
    // ENVIO_BLOQUEADO_COMPLIANCE y CUENTA_TWILIO_INACTIVA: 20003 con el token bueno, la cuenta todavía no está
    // habilitada para enviar (o está suspendida). El texto del backend dice qué falta y que el token está bien; lo
    // que se hace después (reanudar la marca) es en Conexión.
    case "CUENTA_NO_VERIFICADA":
    case "MARCA_FRENADA":
    case "MARCA_DEMO":
    case "ENVIO_BLOQUEADO_COMPLIANCE":
    case "CUENTA_TWILIO_INACTIVA":
      return { texto: l.error || textoError(l), link: { label: "Ir a Configuración", ruta: `${RUTA_CONFIG}?seccion=cuenta` } };
    // DESTINO_INVALIDO (incluye el caso +1) y cualquier otro: el texto del backend.
    default: return { texto: l.error || textoError(l) };
  }
}

export function usePruebaCampana() {
  const [numeroPrueba, setNumeroPrueba] = useState(() => leerStorage(NUMERO_PRUEBA_KEY));
  const [prueba, setPrueba] = useState<ResultadoPrueba | null>(null);
  const [estadoPrueba, setEstadoPrueba] = useState<EstadoPrueba | null>(null);
  const [pruebaInicio, setPruebaInicio] = useState(0);
  const [pruebaVencida, setPruebaVencida] = useState(false);
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const [errorPrueba, setErrorPrueba] = useState<Aviso | null>(null);
  const [sinPrueba, setSinPrueba] = useState(false);

  const vistaPrueba = prueba ? {
    ...prueba,
    estado: estadoPrueba?.estado ?? prueba.estado,
    codigoError: estadoPrueba ? estadoPrueba.codigoError : prueba.codigoError,
    motivoError: estadoPrueba ? estadoPrueba.motivoError : prueba.motivoError,
    simulado: prueba.simulado || !!estadoPrueba?.simulado,
  } : null;
  const titulo = vistaPrueba ? tituloPrueba(vistaPrueba) : null;
  const pruebaOk = pruebaDejaSeguir(vistaPrueba);

  /**
   * Una prueba recuperada de la auditoría trae el estado de cuando salió: se pide el de ahora una vez (el polling de
   * 3 s vale sólo el primer minuto, que después de un F5 quizás ya pasó). Si mientras tanto se mandó otra, la
   * respuesta vieja no la pisa: `pruebaIdRef` dice cuál es la de la pantalla, y quien la cambia (restaurarla,
   * mandar otra) lo anota enseguida, sin esperar al render. Si falla, queda el estado de la auditoría.
   */
  const pruebaIdRef = useRef("");
  pruebaIdRef.current = prueba?.mensajeId || "";
  const refrescarPrueba = useCallback(async (mensajeId: string): Promise<EstadoPrueba | null> => {
    try {
      const l = leerRespuesta<EstadoPrueba>(await oficialApi.estadoPrueba(mensajeId));
      if (l.ok && l.data && pruebaIdRef.current === mensajeId) {
        setEstadoPrueba(l.data);
        return l.data;
      }
    } catch { /* queda el estado de la auditoría */ }
    return null;
  }, []);

  /** La prueba que viajó al editor y volvió (`FotoAsistente`): se muestra como estaba. */
  const restaurar = useCallback((p: ResultadoPrueba, inicio: number) => {
    setPrueba(p);
    setPruebaInicio(inicio);
  }, []);

  /**
   * La prueba de la auditoría (`pruebaDeLaAuditoria`): se muestra y se decide con el estado de AHORA. La auditoría
   * guarda el de cuando salió («aceptado»), y desde el 26/10 sólo un recibo de que llegó deja seguir (`pruebaDejaSeguir`,
   * como el backend). Devuelve la prueba con ese estado (o con el de la auditoría, si no se pudo consultar).
   */
  const retomar = useCallback(async (g: { prueba: ResultadoPrueba; inicio: number }): Promise<ResultadoPrueba> => {
    setPrueba(g.prueba);
    setPruebaInicio(g.inicio);
    pruebaIdRef.current = g.prueba.mensajeId;
    const ahora = await refrescarPrueba(g.prueba.mensajeId);
    return ahora
      ? { ...g.prueba, estado: ahora.estado, codigoError: ahora.codigoError, motivoError: ahora.motivoError, simulado: g.prueba.simulado || !!ahora.simulado }
      : g.prueba;
  }, [refrescarPrueba]);

  /** Otra plantilla: la prueba (y «Seguir sin probar») eran de la anterior. */
  const reiniciar = useCallback(() => {
    setPrueba(null);
    setEstadoPrueba(null);
    setSinPrueba(false);
  }, []);

  const enviarPrueba = async (idCampana: string) => {
    const to = numeroPrueba.trim();
    if (!idCampana || !to || enviandoPrueba) return;
    guardarStorage(NUMERO_PRUEBA_KEY, to);
    setEnviandoPrueba(true);
    setErrorPrueba(null);
    // la consulta de una prueba anterior que vuelva ahora ya no es de la pantalla (ver `refrescarPrueba`)
    pruebaIdRef.current = "";
    // se está probando: «Seguir sin probar» deja de valer (si ésta falla, se vuelve a marcar a propósito)
    setSinPrueba(false);
    setPrueba(null);
    setEstadoPrueba(null);
    setPruebaVencida(false);
    try {
      const l = leerRespuesta<ResultadoPrueba>(await oficialApi.prueba(idCampana, to));
      if (!l.ok) { setErrorPrueba(avisoRechazoPrueba(l)); return; }
      setPrueba(l.data);
      setPruebaInicio(Date.now());
    } catch (e) {
      const x = leerError(e);
      setErrorPrueba({ texto: esErrorDeClave(x.code) ? TEXTO_ERROR_CLAVE : textoError(x) });
    } finally {
      setEnviandoPrueba(false);
    }
  };

  // Cada 3 s hasta un estado final o 60 s: el título se recalcula con cada respuesta.
  const intervaloPrueba = prueba && vistaPrueba && !pruebaTerminada(vistaPrueba.estado) && !pruebaVencida ? 3000 : null;
  const pollPrueba = usePolling(async () => {
    if (!prueba) return;
    if (Date.now() - pruebaInicio > 60_000) { setPruebaVencida(true); return; }
    const mensajeId = prueba.mensajeId;
    const l = leerRespuesta<EstadoPrueba>(await oficialApi.estadoPrueba(mensajeId));
    // mientras volvía, se mandó otra: ésta ya no es la de la pantalla
    if (pruebaIdRef.current !== mensajeId) return;
    if (!l.ok) throw new Error(l.error || "No pudimos consultar la prueba");
    setEstadoPrueba(l.data);
  }, intervaloPrueba, [prueba?.mensajeId]);
  /**
   * «Volver a consultar»: pasado el minuto (o con el servidor que no respondía), pregunta ya y sigue consultando otro
   * minuto. Un borrador no tiene bitácora donde ver el estado final: sin esto, había que mandar otra prueba.
   */
  const reconsultarPrueba = () => {
    if (!prueba) return;
    setPruebaVencida(false);
    setPruebaInicio(Date.now());
    if (pollPrueba.detenido) pollPrueba.reintentar();
    void refrescarPrueba(prueba.mensajeId);
  };

  return {
    numero: numeroPrueba, setNumero: setNumeroPrueba, enviando: enviandoPrueba, error: errorPrueba, enviar: enviarPrueba,
    vista: vistaPrueba, titulo, ok: pruebaOk, inicio: pruebaInicio,
    consultando: intervaloPrueba !== null, vencida: pruebaVencida && !!vistaPrueba && !pruebaTerminada(vistaPrueba.estado),
    pollDetenido: pollPrueba.detenido, reconsultar: reconsultarPrueba,
    sinPrueba, setSinPrueba, restaurar, retomar, reiniciar,
  };
}

export type PruebaCampana = ReturnType<typeof usePruebaCampana>;
